<template>
  <RuesBoxSize
    :size="props.item?.width"
    class="text-left grid grid-cols-1 gap-y-4 product-carousel"
  >
    <UiHeadline
      v-if="props.item?.headline"
      type="h2"
      design="h2"
    >
      {{ props.item?.headline }}
    </UiHeadline>
    <UiHeadline
      v-else
      type="h2"
      design="h2"
    >
      {{ t('headline') }}
    </UiHeadline>
    <div class="transition-all ease-in-out delay-150 min-h-[230px] lg:min-h-[270px] xl:min-h-[307px] 1xl:min-h-[307px] 2xl:min-h-[306px] select-none">
      <div
        v-if="!props.skeleton"
        class="relative"
      >
        <UiCarousel
          class="embla-product"
          ignore-drag-inside=".shared-picture-carousel"
          :slide-size="SLIDE_SIZE_MOBILE"
          :slide-count="total"
          :ssr-breakpoints="ssrBreakpoints"
          :show-arrows="total > 1"
          :options="carouselOptions"
          :accessibility="accessibilityOptions"
        >
          <div
            v-for="article in props.item?.objekte"
            :key="article.id"
            class="embla__slide w-full px-2 py-3"
          >
            <SharedObjectCard
              :nested-carousel="true"
              design="objectslider"
              :lazy-loading="true"
              :builder-carousel="false"
              :lastminute="true"
              class="w-full sm:hidden block"
              :item="article"
            />
            <SharedObjectCard
              :nested-carousel="true"
              design="objectslider"
              :lazy-loading="true"
              :builder-carousel="true"
              :lastminute="true"
              class="w-full hidden sm:block"
              :item="article"
            />
          </div>

          <template
            v-if="total > 1"
            #prev
          >
            <span aria-hidden="true" />
          </template>
          <template
            v-if="total > 1"
            #next
          >
            <span aria-hidden="true" />
          </template>
        </UiCarousel>
      </div>
      <div
        v-show="props.skeleton"
        class="flex flex-nowrap gap-x-4 w-full overflow-hidden"
      >
        <SkeletonSharedObjectCard
          design="objectslider"
          class="w-[75%] md:w-[41.7%] lg:w-[33.3333%]"
        />
        <SkeletonSharedObjectCard
          design="objectslider"
          class="md:w-[41.7%] lg:w-[33.3333%] hidden md:block"
        />
        <SkeletonSharedObjectCard
          design="objectslider"
          class="lg:w-[33.3333%] hidden lg:block"
        />
        <SkeletonSharedObjectCard
          design="objectslidersmall"
          class="w-[22%] md:w-[12.2%] block lg:hidden"
        />
      </div>
    </div>
    <div>
      <div class="inline-block">
        <UiButton
          href="#"
          color="primary"
        >
          {{ t('all_last_minute_offers') }}
        </UiButton>
      </div>
    </div>
  </RuesBoxSize>
</template>

<script setup lang="ts">
import type { EmblaOptionsType } from 'embla-carousel'
import type { AccessibilityOptionsType } from 'embla-carousel-accessibility'
import type { SsrOptionsType } from 'embla-carousel-ssr'
import { BREAKPOINT_LG_MIN_QUERY } from '~/utils/breakpoints'

const t = useTranslations('RuesProductLastminuteCarousel').translate

const SLIDE_SIZE_MOBILE = 76.5
const SLIDE_SIZE_TABLET = 43.5
const SLIDE_SIZE_DESKTOP = 33.333

interface CarouselItem {
  bilder_neu?: string[]
  alt?: string
  objekte?: any[]
  headline?: string
  width?: number
  element?: string
  serp_url?: string
  serp_url_params?: Record<string, any>
  h2_region_ort?: string
}

interface Props {
  item?: CarouselItem | null
  skeleton?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  item: null,
  skeleton: true
})

const total = computed(() => props.item?.objekte?.length ?? 0)

const ssrBreakpoints = computed(() => {
  const n = Math.max(total.value, 0)
  return {
    '(min-width: 768px)': { slideSizes: Array.from({ length: n }, () => SLIDE_SIZE_TABLET) },
    [BREAKPOINT_LG_MIN_QUERY]: { slideSizes: Array.from({ length: n }, () => SLIDE_SIZE_DESKTOP) }
  } as NonNullable<SsrOptionsType['breakpoints']>
})

const carouselOptions = computed<EmblaOptionsType>(() => ({
  align: 'start',
  loop: total.value > 1
}))

const accessibilityOptions = computed<AccessibilityOptionsType>(() => ({
  announceChanges: true,
  carouselAriaLabel: props.item?.headline || t.value('carousel_aria_label'),
  previousButtonAriaLabel: t.value('previous_button_aria_label'),
  nextButtonAriaLabel: t.value('next_button_aria_label'),
  slideAriaLabel: (_grouped, first) => t.value('slide_aria_label', { current: first + 1, total: total.value }),
  liveRegionContent: (_grouped, first) => t.value('live_region_content', { current: first + 1, total: total.value }),
  rootNode: (emblaRoot) => emblaRoot.parentElement
}))
</script>

<style src="./css/RuesProductCarousel.css" scoped></style>
